﻿import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Translate } from '../../../../../../shared/LanguageService';
import { useUserContext } from '../../../../../../shared/context/user-context';
import { useGetProfile } from './hooks/useUpdateUserProfile';
import { Language, ProfileData, TimeZone } from './ProfileApi';
import { handleError } from '../../../../../../shared/ErrorCatching';
import { UserProfile } from './models/profile';
import ProfileContextSmallerScreen from './ui/ProfileContextSmallerScreen';
import ProfileContextLargerScreen from './ui/ProfileContextLargerScreen';
import { useUserProfile } from './hooks/useUserProfile';

export type ProfileContextLabels = {
    loggedInAs: string;
};

type ProfileContextProps = {
    labels: ProfileContextLabels;
};

export type ProfileModalProps = {
    availableLanguages: Language[];
    availableTimeZones: TimeZone[];
    userProfile: UserProfile;
};

function ProfileContextDataLayer({ labels }: ProfileContextProps) {
    let { user } = useUserContext();

    let canFetchProfileInformation = !user.details.isImpersonated;
    let { isError, isLoading, data, error } = useGetProfile<ProfileData>(
        canFetchProfileInformation
    );

    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

    let userProfile = useUserProfile(user, data, [data]);

    if (isLoading) {
        return null;
    }

    if (isError) {
        handleError(error);
    }

    let availableLanguages;
    let availableTimeZones;
    if (data) {
        availableLanguages = data ? data.availableLanguages : null;
        availableTimeZones = data ? data.timeZones : null;
    }

    if (isSmallerScreen) {
        return (
            <ProfileContextSmallerScreen
                labels={labels}
                userProfile={userProfile}
                availableTimeZones={availableTimeZones}
                availableLanguages={availableLanguages}
            />
        );
    }

    return (
        <ProfileContextLargerScreen
            labels={labels}
            userProfile={userProfile}
            availableTimeZones={availableTimeZones}
            availableLanguages={availableLanguages}
        />
    );
}

function TranslatedProfileContext() {
    let labels: ProfileContextLabels = {
        loggedInAs: Translate('header/nav/logged-in-as')
    };
    return <ProfileContextDataLayer labels={labels} />;
}

export default TranslatedProfileContext;
